import { ReactNode } from 'react';
import { TealDiscountTag } from '@images';
import { Skeleton } from '@mui/material';

import { Icon } from '@components';

interface TransactionHistoryItem {
  title: string;
  value: string | ReactNode;
  className?: string;
}

interface TransactionHistoryProps {
  paymentHistoryVal: TransactionHistoryItem[];
  isLoading: boolean;
  type?: string;
  termLabel?: string;
}

const insertTermInSubRows = (
  rows: TransactionHistoryItem[],
  termValue: string
): TransactionHistoryItem[] => {
  const next = [...rows];
  const amountIdx = next.findIndex((r) => r.title === 'Amount');
  const badgeIdx = next.findIndex((r) => Boolean(r.className));
  let insertAt = -1;
  if (amountIdx >= 0) {
    insertAt = amountIdx + 1;
  } else if (badgeIdx >= 0) {
    insertAt = badgeIdx + 1;
  }
  if (insertAt < 0) return next;
  next.splice(insertAt, 0, {
    title: 'Term',
    value: termValue,
    className: ''
  });
  return next;
};

const TransactionHistory = ({
  paymentHistoryVal,
  isLoading,
  type,
  termLabel
}: TransactionHistoryProps) => {
  if (isLoading) {
    return (
      <div className="border-b border-grey10 py-3 last:border-none">
        <Skeleton animation="wave" className="w-full" />
      </div>
    );
  }

  const filtered = paymentHistoryVal.filter((item) => {
    if (item.value == null) return false;
    if (typeof item.value === 'string') {
      const s = item.value.trim();
      return s !== '' && s.toLowerCase() !== 'null';
    }
    return true;
  });
  const termTrim = (termLabel ?? '').trim();
  const rowsWithTerm =
    termTrim === '' ? filtered : insertTermInSubRows(filtered, termTrim);

  const badgeItem = rowsWithTerm.find((item) => Boolean(item.className));
  const amountItem = rowsWithTerm.find((item) => item.title === 'Amount');
  const subItems = rowsWithTerm.filter(
    (item) => item !== badgeItem && item !== amountItem
  );

  const isDiscount = type === 'discount';
  const isClosed = type === 'closed';

  let amountClass = 'text-primary40';
  if (isDiscount) amountClass = 'text-cofeeTeal5';
  if (isClosed) amountClass = 'text-cofeeNeutral10 line-through';

  const renderTypeIcon = () => {
    if (isClosed) {
      return (
        <span className="flex size-6 shrink-0 items-center justify-center rounded-lg bg-cofeeRed1">
          <Icon name="deactivate" size="16" />
        </span>
      );
    }
    if (isDiscount) {
      return (
        <span className="flex size-6 shrink-0 items-center justify-center rounded-lg bg-cofeeTeal1">
          <TealDiscountTag />
        </span>
      );
    }
    return (
      <span className="flex size-6 shrink-0 items-center justify-center rounded-lg bg-primary10">
        <Icon name="credit-card-tick" size="none" />
      </span>
    );
  };

  return (
    <div className="flex w-full min-w-0 flex-col border-b border-grey10 py-3 last:border-none">
      <div className="flex min-w-0 items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {renderTypeIcon()}
          <span className="min-w-0 text-sm font-medium text-cofeeNeutral10">
            {badgeItem?.value}
          </span>
        </div>
        <div
          className={`inline-flex shrink-0 flex-nowrap items-baseline gap-0.5 whitespace-nowrap text-sm font-semibold ${amountClass}`}
        >
          {isDiscount ? '-' : null}
          {amountItem?.value}
        </div>
      </div>
      {subItems.length > 0 && (
        <div className="flex w-full min-w-0 flex-col gap-0.5 pl-8">
          {subItems.map((item) => (
            <div
              key={item.title}
              className="flex min-w-0 items-start justify-between"
            >
              <span className="shrink-0 pr-1 text-xs text-grey">
                {item.title}
              </span>
              <span className="min-w-0 flex-1 text-right text-xs text-grey">
                {item.value}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;
